import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { SETTINGS } from '../constants';
import {
  ApiTestExecution,
  IApiTest,
  IApiTestsResponse,
  IUseCaseTestExecutionResponse,
  UseCaseTestExecution,
} from '../types';

interface TestExecutionsState {
  environmentId: string;
  apiTestExecutions: Record<string, Record<string, ApiTestExecution>>;
  useCaseTestExecutions: Record<string, Record<string, UseCaseTestExecution>>;
  addApiTestExecution: (
    useCaseTag: string,
    testCaseTag: string,
    execution: ApiTestExecution,
  ) => void;
  updateApiTestExecution: (
    useCaseTag: string,
    testCaseTag: string,
    execution: Partial<ApiTestExecution>,
  ) => void;
  removeApiTestExecution: (useCaseTag: string, testCaseTag: string) => void;
  addUseCaseTestExecution: (
    useCaseTag: string,
    testCaseTag: string,
    execution: UseCaseTestExecution,
  ) => void;
  updateUseCaseTestExecution: (
    useCaseTag: string,
    testCaseTag: string,
    execution: Partial<UseCaseTestExecution>,
  ) => void;
  removeUseCaseTestExecution: (useCaseTag: string, testCaseTag: string) => void;
  setApiTestExecutions: (
    executions: Record<string, Record<string, ApiTestExecution>>,
  ) => void;
  setUseCaseTestExecutions: (
    executions: Record<string, Record<string, UseCaseTestExecution>>,
  ) => void;
  getApiTestExecutions: (
    useCaseTag: string,
    testCaseTag: string,
  ) => ApiTestExecution | undefined;
  getUseCaseTestExecutions: (
    useCaseTag: string,
    testCaseTag: string,
  ) => UseCaseTestExecution | undefined;
  addBulkApiTestExecutions: (
    apiTests: IApiTestsResponse,
    environmentId: string,
  ) => void;
  addBulkUseCaseTestExecutions: (
    useCaseTests: IUseCaseTestExecutionResponse,
    environmentId: string,
    cleanState: boolean,
  ) => void;
}

export const useTestExecutionsStore = create<TestExecutionsState>()(
  devtools(
    (set, get) => ({
      environmentId: '',
      apiTestExecutions: {} as Record<string, Record<string, ApiTestExecution>>,
      useCaseTestExecutions: {} as Record<
        string,
        Record<string, UseCaseTestExecution>
      >,
      addApiTestExecution: (
        useCaseTag: string,
        testCaseTag: string,
        execution: ApiTestExecution,
      ) =>
        set((state: TestExecutionsState) => ({
          apiTestExecutions: {
            ...state.apiTestExecutions,
            [useCaseTag]: {
              ...state.apiTestExecutions[useCaseTag],
              [testCaseTag]: execution,
            },
          },
        })),
      updateApiTestExecution: (
        apiTag: string,
        testCaseTag: string,
        execution: Partial<ApiTestExecution>,
      ) =>
        set(
          (state: TestExecutionsState) => {
            const prevBucket = state.apiTestExecutions[apiTag] ?? {};
            const prevExecution =
              prevBucket[testCaseTag] ?? ({} as ApiTestExecution);

            return {
              apiTestExecutions: {
                ...state.apiTestExecutions,
                [apiTag]: {
                  ...prevBucket,
                  [testCaseTag]: {
                    ...prevExecution,
                    ...execution,
                  },
                },
              },
            };
          },
          false,
          'useTestExecutionsStore/updateApiTestExecution',
        ),
      removeApiTestExecution: (useCaseTag: string, testCaseTag: string) =>
        set(
          (state: TestExecutionsState) => {
            const { [testCaseTag]: _, ...rest } =
              state.apiTestExecutions[useCaseTag];
            return {
              apiTestExecutions: {
                ...state.apiTestExecutions,
                [useCaseTag]: rest,
              },
            };
          },
          false,
          'useTestExecutionsStore/removeApiTestExecution',
        ),
      addUseCaseTestExecution: (
        useCaseTag: string,
        testCaseTag: string,
        execution: UseCaseTestExecution,
      ) =>
        set(
          (state: TestExecutionsState) => ({
            useCaseTestExecutions: {
              ...state.useCaseTestExecutions,
              [useCaseTag]: {
                ...state.useCaseTestExecutions[useCaseTag],
                [testCaseTag]: execution,
              },
            },
          }),
          false,
          'useTestExecutionsStore/addUseCaseTestExecution',
        ),
      updateUseCaseTestExecution: (
        useCaseTag: string,
        testCaseTag: string,
        execution: Partial<UseCaseTestExecution>,
      ) => {
        return set(
          (state: TestExecutionsState) => {
            const prevBucket = state.useCaseTestExecutions[useCaseTag] ?? {};
            const prevExecution =
              prevBucket[testCaseTag] ?? ({} as UseCaseTestExecution);

            return {
              useCaseTestExecutions: {
                ...state.useCaseTestExecutions,
                [useCaseTag]: {
                  ...prevBucket,
                  [testCaseTag]: {
                    ...prevExecution,
                    ...execution,
                    error: execution?.error || null,
                  },
                },
              },
            };
          },
          false,
          'useTestExecutionsStore/updateUseCaseTestExecution',
        );
      },
      removeUseCaseTestExecution: (useCaseTag: string, testCaseTag: string) =>
        set(
          (state: TestExecutionsState) => {
            const { [testCaseTag]: _, ...rest } =
              state.useCaseTestExecutions[useCaseTag];
            return {
              useCaseTestExecutions: {
                ...state.useCaseTestExecutions,
                [useCaseTag]: rest,
              },
            };
          },
          false,
          'useTestExecutionsStore/removeUseCaseTestExecution',
        ),
      setApiTestExecutions: (
        executions: Record<string, Record<string, ApiTestExecution>>,
      ) =>
        set(
          () => ({
            apiTestExecutions: executions,
          }),
          false,
          'useTestExecutionsStore/setApiTestExecutions',
        ),
      setUseCaseTestExecutions: (
        executions: Record<string, Record<string, UseCaseTestExecution>>,
      ) =>
        set(
          () => ({
            useCaseTestExecutions: executions,
          }),
          false,
          'useTestExecutionsStore/setUseCaseTestExecutions',
        ),
      getApiTestExecutions: (useCaseTag: string, testCaseTag: string) => {
        const state = get() as TestExecutionsState;
        return state.apiTestExecutions[useCaseTag]?.[testCaseTag];
      },
      getUseCaseTestExecutions: (useCaseTag: string, testCaseTag: string) => {
        const state = get() as TestExecutionsState;
        return state.useCaseTestExecutions[useCaseTag]?.[testCaseTag];
      },
      addBulkApiTestExecutions: (
        apiTests: IApiTestsResponse,
        environmentId: string,
      ) =>
        set(
          (state: TestExecutionsState) => {
            const apiTags = Object.keys(apiTests);
            const updatedExecutions = { ...state.apiTestExecutions };

            for (const apiTag of apiTags) {
              for (const test in apiTests[apiTag]) {
                const { api_test_execution, test_case } =
                  apiTests[apiTag][test];
                if (!updatedExecutions[apiTag]) {
                  updatedExecutions[apiTag] = {};
                }

                if (api_test_execution) {
                  updatedExecutions[apiTag][test_case.tag] = api_test_execution;
                }
              }
            }

            return { apiTestExecutions: updatedExecutions, environmentId };
          },
          false,
          'useTestExecutionsStore/addBulkApiTestExecutions',
        ),
      addBulkUseCaseTestExecutions: (
        useCaseTests: IUseCaseTestExecutionResponse,
        environmentId: string,
        cleanState = false,
      ) =>
        set(
          (state: TestExecutionsState) => {
            const useCaseTags = Object.keys(useCaseTests);
            const updatedExecutions = !cleanState
              ? { ...state.useCaseTestExecutions }
              : {};

            for (const useCaseTag of useCaseTags) {
              for (const test in useCaseTests[useCaseTag]) {
                const { use_case_test_execution, test_case } =
                  useCaseTests[useCaseTag][test];
                if (!updatedExecutions[useCaseTag]) {
                  updatedExecutions[useCaseTag] = {};
                }

                if (use_case_test_execution) {
                  updatedExecutions[useCaseTag][test_case.tag] =
                    use_case_test_execution;
                }
              }
            }

            return { useCaseTestExecutions: updatedExecutions, environmentId };
          },
          false,
          'useTestExecutionsStore/addBulkUseCaseTestExecutions',
        ),
    }),
    {
      name: 'useTestExecutionsStore',
      enabled: SETTINGS.IS_DEV,
    },
  ),
);
